import React from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField, TextFieldVariants, styled } from '@mui/material';
import { CalendarPickerView } from '@mui/lab';

const StyledDatePicker = styled(DatePicker)({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7C0E6F',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7C0E6F',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7C0E6F',
  },
  '& .css-14s5rfu-MuiFormLabel-root-MuiInputLabel-root': {
    fontSize: '0.85em',
    fontFamily: 'Lato',
  },
  '& label.Mui-focused': {
    color: '#7C0E6F',
  },
  '& .css-nxo287-MuiInputBase-input-MuiOutlinedInput-input': {
    fontSize: '0.85em',
    fontFamily: 'Lato',
  },
});

import './styles.css';
import {
  baseDateTimeFormatting,
  convertDateToUTC,
  convertUtcToDate,
} from '../../common/helper';

interface Props {
  value?: string | Date;
  label?: string;
  inputFormat?: string;
  disableFuture?: boolean;
  minDate?: string | Date;
  readOnly?: boolean;
  disabled?: boolean;
  width?: string;
  className?: string;
  fontSize?: string;
  defaultValue?: string | Date;
  supportUTC?: boolean;
  timezoneOffset?: number;
  size?: 'small' | 'medium';
  variant?: TextFieldVariants;
  views?: CalendarPickerView[] | undefined;
  onChange: (date: Date | string) => void;
}

export function DatePickerInput({
  views,
  label,
  inputFormat,
  disableFuture = false,
  minDate,
  readOnly,
  disabled,
  className = '',
  width,
  fontSize,
  size = 'medium',
  variant = 'outlined',
  supportUTC = true,
  timezoneOffset,
  defaultValue,
  onChange,
}: Props) {
  const [date, setDate] = React.useState<null | Date | string>(null);

  const handleChange = (_date: unknown) => {
    setDate(_date as Date);

    if (supportUTC) {
      const result = convertDateToUTC(
        _date,
        baseDateTimeFormatting,
        timezoneOffset,
        true,
      );

      onChange?.(result);
    } else {
      const selectedDate = new Date(_date as string);

      onChange?.(selectedDate);
    }
  };

  React.useEffect(() => {
    if (defaultValue && !date) {
      const date = convertUtcToDate(
        defaultValue,
        baseDateTimeFormatting,
        timezoneOffset,
        false,
      );

      setDate(date);
    }
  }, [defaultValue, timezoneOffset]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledDatePicker
        label={label || ''}
        readOnly={readOnly}
        disabled={disabled}
        value={date}
        minDate={minDate}
        disableFuture={disableFuture}
        className={`padding-0 ${className}`}
        views={views ?? ['year', 'month', 'day']}
        inputFormat={inputFormat ?? 'DD MMM, YYYY'}
        InputProps={{
          disableUnderline: true,
          style: {
            fontSize: fontSize || undefined,
          },
        }}
        renderInput={({ ...params }) => (
          <TextField
            {...params}
            style={{
              width: width ?? '130px',
            }}
            className={`custom-date-picker ${className}`}
            inputProps={{
              ...params.inputProps,
              placeholder: inputFormat ?? 'DD MMM, YYYY'.toLowerCase(),
            }}
            size={size}
            variant={variant}
            onKeyDown={(e) => {
              e.preventDefault()
            }}
          />
        )}
        onChange={handleChange}
      />
    </LocalizationProvider>
  );
}
